import { useEffect, useRef, useState } from 'react'

// Ticks are kept on this device for a while, so a page refresh mid-cooking keeps them,
// and the next time someone makes the recipe it starts fresh
const KEEP_HOURS = 12
const storageKey = (recipeId) => `ingredientsChecked:${recipeId}`

const loadChecked = (recipeId) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey(recipeId)) || 'null')
    if (saved && Date.now() - saved.at < KEEP_HOURS * 3600 * 1000) {
      return saved.checked || []
    }
  } catch (e) {
    // Private browsing or blocked storage: ticks just won't survive a refresh
  }
  return []
}

const saveChecked = (recipeId, checked) => {
  try {
    if (checked.length) {
      window.localStorage.setItem(storageKey(recipeId), JSON.stringify({ checked, at: Date.now() }))
    } else {
      window.localStorage.removeItem(storageKey(recipeId))
    }
  } catch (e) {
    // See loadChecked
  }
}

// Ticking this many off means they're really cooking (one could be a stray tap)
const COOKING_AFTER = 2

// The recipe's ingredients as a list the cook can tick off as they use each one.
// onStartedCooking() is called once per visit, when they tick off a second ingredient.
const IngredientChecklist = ({ recipeId, ingredients = [], onStartedCooking }) => {
  const [checked, setChecked] = useState([])
  const reported = useRef(false)

  useEffect(() => {
    setChecked(loadChecked(recipeId))
    reported.current = false
  }, [recipeId])

  const toggle = (index) => {
    const ticking = !checked.includes(index)
    const next = ticking ? [...checked, index] : checked.filter((i) => i !== index)
    setChecked(next)
    saveChecked(recipeId, next)
    if (
      ticking &&
      !reported.current &&
      onStartedCooking &&
      next.length >= Math.min(COOKING_AFTER, ingredients.length)
    ) {
      reported.current = true
      onStartedCooking()
    }
  }

  const clearAll = () => {
    setChecked([])
    saveChecked(recipeId, [])
  }

  if (!ingredients.length) return null

  return (
    <>
      <ul className="ingredientChecklist">
        {ingredients.map(({ name, measurement, quantity }, index) => {
          const done = checked.includes(index)
          return (
            <li key={index}>
              <label className={`ingredientCheck ${done ? 'done' : ''}`}>
                <input type="checkbox" checked={done} onChange={() => toggle(index)} />
                <span className="ingredientCheckBox" aria-hidden="true" />
                <span className="ingredientCheckText">
                  {[quantity, measurement, name].filter(Boolean).join(' ')}
                </span>
              </label>
            </li>
          )
        })}
      </ul>
      {checked.length ? (
        <p className="ingredientChecklistProgress">
          {checked.length} of {ingredients.length} used ·{' '}
          <button type="button" className="ingredientChecklistClear" onClick={clearAll}>
            Uncheck all
          </button>
        </p>
      ) : (
        <p className="ingredientChecklistHint">Tap each one as you use it.</p>
      )}
    </>
  )
}

export default IngredientChecklist
